import { inject, Injectable, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BackendService } from '@ipg/ngx-frontend-toolset';
import { InteractionFilterMode, InteractionStatus } from '../models/interactions.model';

export interface TagResponse {
  id: string;
  label: string;
  description?: string;
  deleteAt?: string;
  status?: 'active' | 'inactive';
}

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private readonly _backend = inject(BackendService);
  private readonly _tags = signal<TagResponse[]>([]);
  private readonly _pendingDeletionTags = signal<TagResponse[]>([]);
  private readonly _inactiveTags = signal<TagResponse[]>([]);

  readonly tags = this._tags.asReadonly();
  readonly pendingDeletionTags = this._pendingDeletionTags.asReadonly();
  readonly inactiveTags = this._inactiveTags.asReadonly();

  getAll(): Observable<TagResponse[]> {
    return this._backend
      .get<TagResponse[]>('/tags')
      .pipe(tap((tags) => this._tags.set(tags)));
  }

  getPendingDeletionTags(): Observable<TagResponse[]> {
    return this._backend
      .get<TagResponse[]>('/tags/pending-deletion')
      .pipe(tap((tags) => this._pendingDeletionTags.set(tags)));
  }

  getInactiveTags(): Observable<TagResponse[]> {
    return this._backend
      .get<TagResponse[]>('/tags/inactive')
      .pipe(tap((tags) => this._inactiveTags.set(tags)));
  }

  createTag(data: { label: string; description?: string }): Observable<TagResponse> {
    return this._backend
      .post<TagResponse>('/tags', data)
      .pipe(tap((created) => this._tags.update((tags) => [...tags, created])));
  }

  updateTag(
    tagId: string,
    data: { label: string; description?: string }
  ): Observable<TagResponse> {
    return this._backend
      .put<TagResponse>(`/tags/${tagId}`, data)
      .pipe(
        tap((updated) =>
          this._tags.update((tags) => tags.map((t) => (t.id === tagId ? updated : t)))
        )
      );
  }

  deleteTag(tagId: string): Observable<TagResponse> {
    return this._backend.delete<TagResponse>(`/tags/${tagId}`).pipe(
      tap((deleted) => {
        this._tags.update((tags) => tags.filter((t) => t.id !== tagId));
        this._inactiveTags.update((tags) => tags.filter((t) => t.id !== tagId));
        this._pendingDeletionTags.update((tags) => [...tags, deleted]);
      })
    );
  }

  deactivateTag(tagId: string): Observable<TagResponse> {
    return this._backend.post<TagResponse>(`/tags/${tagId}/deactivate`, {}).pipe(
      tap((deactivated) => {
        this._tags.update((tags) => tags.filter((t) => t.id !== tagId));
        this._inactiveTags.update((tags) => [...tags, deactivated]);
      })
    );
  }

  activateTag(tagId: string): Observable<TagResponse> {
    return this._backend.post<TagResponse>(`/tags/${tagId}/activate`, {}).pipe(
      tap((activated) => {
        this._inactiveTags.update((tags) => tags.filter((t) => t.id !== tagId));
        this._tags.update((tags) => [...tags, activated]);
      })
    );
  }

  restoreTag(tagId: string): Observable<TagResponse> {
    return this._backend.post<TagResponse>(`/tags/${tagId}/restore`, {}).pipe(
      tap((restored) => {
        this._pendingDeletionTags.update((tags) => tags.filter((t) => t.id !== tagId));
        if (restored.status === 'inactive') {
          this._inactiveTags.update((tags) => [...tags, restored]);
        } else {
          this._tags.update((tags) => [...tags, restored]);
        }
      })
    );
  }

  getTagRefCount(tagId: string): Observable<{ usages: number; scmBlocks: number }> {
    return this._backend.get<{ usages: number; scmBlocks: number }>(
      `/tags/${tagId}/refs/count`
    );
  }

  getUsedTags(params?: {
    clientId?: string;
    mode?: InteractionFilterMode;
    status?: InteractionStatus[];
    includeShared?: boolean;
  }): Observable<TagResponse[]> {
    const httpParams: Record<string, string | string[]> = {};
    if (params?.clientId) {
      httpParams['clientId'] = params.clientId;
    }
    if (params?.mode) {
      httpParams['mode'] = params.mode;
    }
    if (params?.status?.length) {
      httpParams['status'] = params.status;
    }
    if (params?.includeShared) {
      httpParams['includeShared'] = 'true';
    }
    return this._backend.get<TagResponse[]>('/tags/used', { params: httpParams });
  }

  getInteractionTags(interactionId: string): Observable<TagResponse[]> {
    return this._backend.get<TagResponse[]>(`/interactions/${interactionId}/tags`);
  }

  updateInteractionTags(interactionId: string, tagIds: string[]): Observable<void> {
    return this._backend.put<void>(`/interactions/${interactionId}/tags`, { tagIds });
  }

  getInteractionTagsBatch(
    interactionIds: string[]
  ): Observable<Record<string, TagResponse[]>> {
    return this._backend.post<Record<string, TagResponse[]>>(
      '/interactions/tags/batch',
      { tagIds: interactionIds }
    );
  }
}
